import React, { useEffect, useMemo, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { CURRENCY } from '@/app/utils/constant';
import { getNameAndType } from './utils';
import TrackingContext from './tracking-context';

const usePrevious = (value) => {
  const ref = useRef<any>();
  // the previous page's name is the ref.current before the current page is mounted.
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current || '';
};

function Track(props: { lang: string; children: React.ReactNode }) {
  const langCode = props.lang;
  const general = {
    currencyCode: CURRENCY, // [String] Required, User's currency code, If there is CK_CURRENCY_CODE cookie or CK_CURRENCY_CODE_NEW,Value from CK_CURRENCY_CODE cookie or CK_CURRENCY_CODE_NEW cookie value, If not, generate the corresponding Currency Code based on CK_LANG_CODE.
    langCode: langCode, // [String] Required, Value from CK_LANG_CODE cookie
  };

  const pathname = usePathname();
  let previousPageName = usePrevious(pathname);

  // the page object is stored before the current page is mounted so that the previous page name can be preserved.
  const page = useMemo(() => {
    let previousPageType = '';

    const segments = pathname.split('/').slice(1);
    const value = getNameAndType(segments);
    const name = value.name;
    const type = value.type;

    // document.referrer is cleared only when refreshing. Otherwise, it is always the same value without changing.
    const referrer = typeof window !== 'undefined' && document.referrer ? document.referrer : '';
    // Four scenarios to consider
    // 1. From AEM pages to apac pages
    // 2. From apac pages to AEM pages
    // 3. From apac pages to apac pages by next link
    // 3. From apac pages to apac pages by a tag

    if (previousPageName) {
      const segments = previousPageName.split('/').slice(1);
      const value = getNameAndType(segments);
      previousPageType = value.type;
      previousPageName = value.name;
    } else if (previousPageName === '' && referrer !== '') {
      // in case the previous page is from <a> tag.
      try {
        // in case the url of referrer is not valid by using URL object to get the pathname
        let segments: string[] = new URL(referrer).pathname.split('/').slice(1);
        if (Array.isArray(segments) && segments.length > 0) {
          if (segments[0] === 'apac') {
            segments = segments.slice(1);
          }
          const value = getNameAndType(segments);
          previousPageType = value.type;
          previousPageName = value.name;
        }
      } catch (e) {
        console.log('url is not valid');
      }
    }

    if (name === previousPageName || previousPageName === '') {
      previousPageName = 'no previous value';
      previousPageType = 'no previous value';
    }

    const page = {
      name,
      type,
      previousPageName: previousPageName,
      previousPageType: previousPageType,
    };
    return page;
  }, [pathname]);

  return (
    <>
      <TrackingContext.Provider value={{ general, page }}>{props.children}</TrackingContext.Provider>
    </>
  );
}

export default React.memo(Track);
